import { CellRenderer, Shape } from '@maxgraph/core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ShapeConstructor = new (...args: any[]) => Shape;

/**
 * Global shape registry. Wraps maxGraph's CellRenderer.registerShape with
 * idempotency and duplicate-detection.
 *
 * Registration is static (global). Multiple WorkflowGraph instances share
 * the same registry — matching maxGraph's own model.
 */
export class ShapeRegistry {
  private static registered = new Map<string, ShapeConstructor>();

  /**
   * Register a shape under a name. Idempotent — registering the same
   * (name, class) pair twice is a no-op. Registering a different class
   * under an existing name throws.
   */
  static register(name: string, shapeClass: ShapeConstructor): void {
    const existing = this.registered.get(name);
    if (existing && existing !== shapeClass) {
      throw new Error(
        `ShapeRegistry: name "${name}" already registered with a different class`
      );
    }
    if (existing) return;
    this.registered.set(name, shapeClass);
    // CellRenderer.registerShape expects `typeof Shape` but our constructor
    // type is broader to accommodate subclasses with different constructor
    // signatures (e.g. RectangleShape).
    CellRenderer.registerShape(name, shapeClass as typeof Shape);
  }

  /** True if a shape with this name is registered. */
  static has(name: string): boolean {
    return this.registered.has(name);
  }

  /** For tests / debugging only. */
  static list(): string[] {
    return Array.from(this.registered.keys());
  }

  /** Removes all registrations. For test cleanup only — not safe in production. */
  static _reset(): void {
    this.registered.clear();
  }
}
